import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { TrendingUp, Check, Undo2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { PageHeader, Avatar, LevelBadge, EmptyState } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function Advancement() {
  const { user } = useAuth();
  const isDirector = user?.role === "director";
  const [reqs, setReqs] = useState([]);
  const [dancers, setDancers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [styles, setStyles] = useState([]);

  const load = () => {
    api.get("/curriculum/advancement-requests").then((r) => setReqs(r.data));
    api.get("/people/dancers").then((r) => setDancers(r.data));
    api.get("/curriculum/levels").then((r) => setLevels(r.data));
    api.get("/curriculum/styles").then((r) => setStyles(r.data));
  };
  useEffect(load, []);

  const dancerMap = Object.fromEntries(dancers.map((d) => [d.id, d]));
  const levelMap = Object.fromEntries(levels.map((l) => [l.id, l]));
  const styleMap = Object.fromEntries(styles.map((s) => [s.id, s]));

  const review = async (id, action) => {
    try { await api.post(`/curriculum/advancement-requests/${id}/review`, { action, notes: "" }); toast.success(action === "approve" ? "Approved & advanced" : "Returned for more info"); load(); }
    catch (e) { toast.error("Error"); }
  };

  const pending = reqs.filter((r) => r.status === "Pending");
  const done = reqs.filter((r) => r.status !== "Pending");

  return (
    <div className="stagger-in">
      <PageHeader title="Level Advancement" subtitle={isDirector ? "Review and approve dancer level advancements." : "Track your recommendations."} testid="advancement-page" />

      <h3 className="font-head font-bold text-lg mb-3">Pending</h3>
      {pending.length === 0 ? <EmptyState icon={TrendingUp} title="No pending requests" hint="You're all caught up!" /> :
        <div className="space-y-3">
          {pending.map((r) => {
            const d = dancerMap[r.dancer_id];
            return (
              <div key={r.id} data-testid={`advancement-req-${r.id}`} className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                <div className="flex items-center gap-3">
                  <Avatar first={d?.first_name} last={d?.last_name} size={46} />
                  <div>
                    <p className="font-head font-bold">{d?.first_name} {d?.last_name}</p>
                    <p className="text-sm text-muted-foreground flex items-center gap-2">{styleMap[r.style_id]?.name} · <LevelBadge name={levelMap[r.to_level_id]?.name} color={levelMap[r.to_level_id]?.color} small /></p>
                  </div>
                </div>
                {isDirector ? (
                  <div className="flex gap-2">
                    <Button data-testid={`approve-${r.id}`} onClick={() => review(r.id, "approve")} className="rounded-full bg-teal hover:bg-teal-dark"><Check className="w-4 h-4 mr-1" />Approve</Button>
                    <Button data-testid={`return-${r.id}`} variant="outline" onClick={() => review(r.id, "return")} className="rounded-full"><Undo2 className="w-4 h-4 mr-1" />Return</Button>
                  </div>
                ) : <Badge className="bg-yellow-50 text-yellow-700 rounded-full">Awaiting Director</Badge>}
              </div>
            );
          })}
        </div>}

      {done.length > 0 && <>
        <h3 className="font-head font-bold text-lg mt-8 mb-3">Recently reviewed</h3>
        <div className="space-y-2">
          {done.map((r) => {
            const d = dancerMap[r.dancer_id];
            return (
              <div key={r.id} className="bg-white rounded-xl border border-gray-100 px-5 py-3 flex items-center justify-between">
                <span className="font-semibold text-sm">{d?.first_name} {d?.last_name} · {styleMap[r.style_id]?.name}</span>
                <Badge className={`rounded-full ${r.status === "Approved" ? "bg-teal-light text-teal-dark" : "bg-gray-100 text-gray-600"}`}>{r.status}</Badge>
              </div>
            );
          })}
        </div>
      </>}
    </div>
  );
}
